import {zodResolver} from "@hookform/resolvers/zod";
import React, {useEffect, useRef, useState} from "react";
import {useForm} from "react-hook-form";
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import * as z from "zod";
import ErrorMessage from "../../../components/ErrorMessage";
import PageLoader from "../../../components/pageLoader";
import Tinymce from "../../../components/tinymce";
import base64ToFile from "../../../services/base64ToFile";
import isCancel from "../../../services/http";
import httpServices from "../../../services/http-services";

const MAX_FILE_SIZE = 1000000;
const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];
const SERVICE_NAME_MAX_LENGTH = 30;
const TIME_OF_SETTLEMENT_MAX_LENGTH = 20;
const APPLICATION_FEE_MAX_LENGTH = 20;

const schema = z.object({
  imageBase64: z
  .any()
  .refine((file) => file instanceof File, "Required")
  .refine(
      (file) => ACCEPTED_IMAGE_TYPES.includes(file?.type),
      "Only .jpg, .jpeg, .png and .webp formats are supported."
  )
  .refine((file) => file?.size <= MAX_FILE_SIZE, "Max image size is 1MB."),
  name: z
  .string()
  .trim()
  .min(1, "Required")
  .max(SERVICE_NAME_MAX_LENGTH, {
    message: `Max length is ${SERVICE_NAME_MAX_LENGTH}`,
  }),
  settlementTime: z
  .string()
  .max(TIME_OF_SETTLEMENT_MAX_LENGTH, {
    message: `Max length is ${TIME_OF_SETTLEMENT_MAX_LENGTH}`,
  })
  .optional(),
  applicationFee: z
  .string()
  .max(APPLICATION_FEE_MAX_LENGTH, {
    message: `Max length is ${APPLICATION_FEE_MAX_LENGTH}`,
  })
  .optional(),
  necessaryDoc: z.string().optional(),
  workProcedure: z.string().optional(),
});

const ServiceDetailsUpdate = () => {
  const navigate = useNavigate();
  const {id} = useParams();
  const [data, setData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [imageTag, setImageTag] = useState("");

  const editorRefNecessaryDoc = useRef(null);
  const editorRefWorkProcedure = useRef(null);

  const {
    register,
    handleSubmit,
    formState: {errors},
    setValue,
    getValues,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      imageBase64: null,
      imageTag: "",
      necessaryDoc: "",
      workProcedure: "",
      settlementTime: "",
      applicationFee: "",
    },
  });

  useEffect(() => {
    httpServices
    .getOne(`/services/${id}`)
    .then(({data: {payload}}) => {
      setData(payload);
      const imageFile = base64ToFile(
          `data:image/jpeg;base64,${payload.imageBase64}`
      );
      setImagePreview(`data:image/jpeg;base64,${payload.imageBase64}`);
      setImageTag(payload.imageTag);
      setValue("imageBase64", imageFile);
      setValue("name", payload.name);
      setValue("necessaryDoc", payload.necessaryDoc);
      setValue("workProcedure", payload.workProcedure);
      setValue("settlementTime", payload.settlementTime);
      setValue("applicationFee", payload.applicationFee);
      setLoading(false);
    })
    .catch((error) => {
      if (!isCancel(error)) {
        setError(error.message);
        toast.error("Fetch failed!");
      }
      setLoading(false);
    });
  }, []);

  const handleChangeIcon = (event) => {
    const file = event.target.files[0];
    if (file) {
      setValue("imageBase64", file);
      setImageTag(file.name);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = (data) => {
    const {imageBase64: image, name, settlementTime, applicationFee} = data;

    const formData = new FormData();
    formData.append("image", image);
    formData.append("name", name);
    formData.append(
        "workProcedure",
        editorRefWorkProcedure.current.getContent()
    );
    formData.append("necessaryDoc", editorRefNecessaryDoc.current.getContent());
    formData.append("settlementTime", settlementTime);
    formData.append("applicationFee", applicationFee);

    setLoading(true);
    httpServices
    .update(`/administrative/services/${id}`, formData)
    .then(() => {
      toast.success("Successfully Updated!");
      setLoading(false);
      navigate("/admin/services");
    })
    .catch((error) => {
      if (isCancel(error)) return;
      setError(error.message);
      setLoading(false);
      toast.error("Update Failed!");
    });
  };

  if (isLoading) return <PageLoader/>;
  if (error)
    return <ErrorMessage message="An error occurred while fetching data."/>;

  return (
      <div className="services-update-section">
        <div className="custom-container">
          <div className="content-section">
            <h1 className="page-title">{data?.name}</h1>
            <div className="content-body">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                  <div className="col-xl-12 form__div-container">
                    <div className="service-image-container">
                      <img
                          className="service-icon"
                          src={imagePreview}
                          alt={imageTag}
                      />
                      <div className="image-uploader">
                        <label className="icon-update-btn" htmlFor="uploader">
                          <i className="fa fa-pencil"></i>
                        </label>
                        <input
                            id="uploader"
                            className="d-none"
                            type="file"
                            accept={ACCEPTED_IMAGE_TYPES.join(", ")}
                            {...register("imageBase64", {
                              onChange: handleChangeIcon,
                            })}
                        />
                      </div>
                    </div>
                    <ErrorMessage message={errors.imageBase64?.message}/>
                  </div>
                  <div className="col-xl-12 form__div-container">
                    <div className="form__div">
                      <input
                          type="text"
                          className="form__input"
                          {...register("name")}
                      />
                      <label htmlFor="name" className="form__label">
                        Service Name
                      </label>
                    </div>
                    <ErrorMessage message={errors.name?.message}/>
                  </div>
                  <div className="col-xl-12 form__div-container">
                    <h5>Necessary Documents</h5>
                    <Tinymce
                        editorRef={editorRefNecessaryDoc}
                        onEditorChange={(content) =>
                            setValue("necessaryDoc", content)
                        }
                        value={getValues("necessaryDoc") || ""}
                    />
                    <ErrorMessage message={errors.necessaryDoc?.message}/>
                  </div>
                  <div className="col-xl-12 form__div-container">
                    <h5>Work Procedure</h5>
                    <Tinymce
                        editorRef={editorRefWorkProcedure}
                        onEditorChange={(content) =>
                            setValue("workProcedure", content)
                        }
                        value={getValues("workProcedure") || ""}
                    />
                    <ErrorMessage message={errors.workProcedure?.message}/>
                  </div>
                  <div className="col-xl-6 form__div-container">
                    <div className="form__div">
                      <input
                          type="text"
                          className="form__input"
                          {...register("settlementTime")}
                      />
                      <label htmlFor="settlementTime" className="form__label">
                        Time of Settlement
                      </label>
                    </div>
                    <ErrorMessage message={errors.settlementTime?.message}/>
                  </div>
                  <div className="col-xl-6 form__div-container">
                    <div className="form__div">
                      <input
                          type="text"
                          className="form__input"
                          {...register("applicationFee")}
                      />
                      <label htmlFor="applicationFee" className="form__label">
                        Application Fee
                      </label>
                    </div>
                    <ErrorMessage message={errors.applicationFee?.message}/>
                  </div>
                  <div className="col-xl-12 text-right">
                    <button type="submit" className="save-btn">
                      Submit
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
  );
};

export default ServiceDetailsUpdate;
